import React from 'react';
import SingleDatePickerInBody from 'components/forms/SingleDatePickerInBody';
import moment from 'moment-timezone';

/** https://reactdatepicker.com/ */
import ReactDatePicker from 'react-datepicker';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import de from 'date-fns/locale/de';
import enUS from 'date-fns/locale/en-US';
import es from 'date-fns/locale/es';
import fr from 'date-fns/locale/fr';
import it from 'date-fns/locale/it';
import nl from 'date-fns/locale/nl';
import pl from 'date-fns/locale/pl';
import tr from 'date-fns/locale/tr';

const availableLocales = {
  de,
  en: enUS,
  es,
  fr,
  it,
  nl,
  pl,
  tr,
};

Object.entries(availableLocales).forEach(([code, localeObj]) => {
  registerLocale(code, localeObj);
});

setDefaultLocale('de');

const registerDatePickerLocale = (localeCode) => {
  if (availableLocales[localeCode]) {
    registerLocale(localeCode, availableLocales[localeCode]);
  } else {
    console.warn(`Locale "${localeCode}" not supported. Falling back to default.`);
    if (!availableLocales.de) {
      registerLocale('de', de);
    }
    registerLocale(localeCode, availableLocales.de);
  }
};

export default class DatePicker extends React.Component {
  constructor(props) {
    super(props);

    moment.locale(globalLocale);

    this.state = {
      // value: moment.tz(props.value, props.returnTz).toISOString(),
      selectedDate: props.value ? moment.tz(props.value, props.returnTz).toDate() : null,

      focused: false,
    };
  }

  componentDidMount() {
    registerDatePickerLocale(this.props.locale);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.locale !== this.props.locale) {
      registerDatePickerLocale(this.props.locale);
    }
  }

  checkFocused = () => {
    return this.state.focused ? 'focused ' : '';
  };

  checkRequired = () => {
    return this.props.meta.validate.rules.indexOf('required') !== -1 ? 'required ' : '';
  };

  checkError = () => {
    return this.props.validation.valid ? '' : 'has-error ';
  };

  // check if user may read this field
  checkAccess = () => {
    return this.props.meta.acl.read;
  };

  // check if user may write to this field
  checkReadOnly = () => {
    return !this.props.meta.acl.write;
  };

  handleDateChange = (date) => {
    this.setState({ selectedDate: date }, () => {
      if (date) {
        const momentObj = moment.tz(date, this.props.displayTz).tz(this.props.returnTz);
        this.props.onChange(this.props.name, momentObj);
      } else if (this.props.showClearDate) {
        this.props.onChange(this.props.name, null);
      }
    });
  };

  handleFocusChange = (focused) => {
    this.setState({ focused });
  };

  handleDateSelect = (date) => {
    const { name, value, locale, showClearDate, displayTz, returnTz, onChange } = this.props;

    if (date) {
      const oldDate = moment.tz(value, returnTz);
      const newDate = moment.tz(date, displayTz).tz(returnTz);
      newDate.hours(oldDate.hours());
      newDate.minutes(oldDate.minutes());
      onChange(name, newDate);

      date.locale(locale);
      this.setState({
        value: moment.tz(date, displayTz).tz(returnTz).toISOString(),
      });
    } else if (showClearDate) {
      onChange(name, null);
      this.setState({
        value: null,
      });
    }
  };

  checkDisabled = () => {
    const { disabled, meta } = this.props;
    if (disabled) {
      return true;
    }
    if (!meta.acl.write) {
      return true;
    }
    return false;
  };

  renderSingleDatePicker = () => {
    const {
      label,
      name,
      isDayBlocked,
      returnTz,
      displayTz,
      value,
      minDate,
      maxDate,
      placeholder,
      showClearDate,
      locale,
    } = this.props;
    const { selectedDate, focused } = this.state;
    const displayTzValue = moment.tz(value, returnTz).tz(displayTz);
    return (
      <ReactDatePicker
        selected={selectedDate}
        onChange={this.handleDateChange}
        // onFocus={() => this.handleFocusChange(true)}
        // onBlur={() => this.handleFocusChange(false)}
        // open={focused}
        placeholderText={placeholder}
        dateFormat="dd.MM.yyyy"
        locale={locale}
        isClearable={showClearDate}
        minDate={minDate ? moment.tz(minDate, 'UTC').toDate() : undefined}
        maxDate={maxDate ? moment.tz(maxDate, 'UTC').toDate() : undefined}
        disabled={this.checkDisabled()}
      />
    );
  };

  render() {
    const {
      label,
      name,
      className,
      style,
      isDayBlocked,
      appendToBody,
      value,
      showClearDate,
      placeholder,
      returnTz,
      displayTz,
      locale,
      minDate,
      maxDate,
    } = this.props;
    const displayTzValue = moment.tz(value, returnTz).tz(displayTz);
    const { selectedDate, focused } = this.state;
    // Todo CD-1440 or CodeAN-999 fix warning - A locale object was not found for the provided string ["de"]. Error Component Stack
    //     at DatePicker (index.es.js:4175:1)
    console.log('locale', locale);
    const _date = new Date();

    return (
      <div
        className={
          'cursor form-group form-group-default ' +
          this.checkFocused() +
          this.checkRequired() +
          this.checkError() +
          (this.checkDisabled() ? 'disabled ' : '') +
          ' ' +
          className
        }
        style={{ overflow: 'visible', paddingBottom: appendToBody ? '4px' : '3px', ...style }}
      >
        <label>{label}</label>
        <div className="lh-25">
          {appendToBody ? (
            <SingleDatePickerInBody
              date={value && displayTzValue}
              onDateChange={this.handleDateSelect}
              focused={focused}
              onFocusChange={({ focused }) => this.setState({ focused })}
              id={label + name}
              numberOfMonths={1}
              noBorder
              block
              small
              hideKeyboardShortcutsPanel
              verticalSpacing={0}
              isOutsideRange={this.isOutsideRange}
              isDayBlocked={isDayBlocked}
              showClearDate={showClearDate}
              placeholder={placeholder}
            />
          ) : (
            <ReactDatePicker
              selected={selectedDate}
              onChange={this.handleDateChange}
              // onFocus={() => this.handleFocusChange(true)}
              // onBlur={() => this.handleFocusChange(false)}
              // open={focused}
              placeholderText={placeholder}
              dateFormat="dd.MM.yyyy"
              locale={locale}
              isClearable={showClearDate}
              minDate={minDate ? moment.tz(minDate, 'UTC').toDate() : undefined}
              maxDate={maxDate ? moment.tz(maxDate, 'UTC').toDate() : undefined}
              disabled={this.checkDisabled()}
            />
          )}
        </div>
      </div>
    );
  }
}
DatePicker.defaultProps = {
  label: '',
  name: '',
  meta: { validate: { rules: '' }, acl: { read: true, write: true } },
  validation: { valid: true },
  value: null,
  onChange: () => {},
  disabled: false,
  displayTz: 'UTC',
  returnTz: 'UTC',
  className: '',
  style: {},
  locale: 'en',
  showClearDate: false,
};
